import { ChevronRight, Description, Folder, FolderOpen } from "@mui/icons-material"
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  alpha,
  useTheme,
} from "@mui/material"
import { Chapter, ChapterType } from "graphql/types"
import { useSearchParams } from "react-router-dom"
import { TransitionGroup } from "react-transition-group"

type HierarchicalChapterListProps = {
  chapters: Chapter[]
  updateChapter: (newChapter: Chapter) => void
}

export const HierarchicalChapterList = (props: HierarchicalChapterListProps) => {
  const { chapters, updateChapter } = props
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()

  // Track selected chapter
  const selectedChapterId = searchParams.get("chapterID")

  const handleExpandClick = (e: React.MouseEvent, chapter: Chapter) => {
    e.stopPropagation()
    const newChapter = { ...chapter, open: !chapter.open }
    updateChapter(newChapter)
  }

  const handleOpenClick = (chapter: Chapter, examId: string | undefined) => {
    const newSearchParams = new URLSearchParams(searchParams.toString())
    newSearchParams.set("chapterID", chapter.id)
    if (chapter.examinationId) {
      newSearchParams.set("examinationID", chapter.examinationId)
    } else if (examId) {
      newSearchParams.set("examinationID", examId)
    }

    setSearchParams(newSearchParams)
  }

  const renderChapters = (
    chapterList: Chapter[],
    depth: number = 0,
    examId?: string
  ): React.ReactNode => {
    return (
      <List component="div" disablePadding>
        <TransitionGroup>
          {chapterList.map((chapter) => {
            const hasSubChapters = chapter.subChapter && chapter.subChapter.length > 0
            const isSelected = selectedChapterId === chapter.id

            return (
              <Collapse key={chapter.id}>
                <ListItemButton
                  onClick={() => handleOpenClick(chapter, examId)}
                  selected={isSelected}
                  sx={{
                    pl: depth * 3 + 2,
                    borderRadius: 1,
                    mb: 0.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      transform: "translateX(4px)",
                    },
                    "&.Mui-selected": {
                      backgroundColor: alpha(theme.palette.primary.main, 0.15),
                      borderLeft: `3px solid ${theme.palette.primary.main}`,
                      "&:hover": {
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      },
                    },
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 36,
                      color: isSelected ? theme.palette.primary.main : "inherit",
                    }}
                  >
                    {hasSubChapters ? (
                      <Box
                        onClick={(e) => handleExpandClick(e, chapter)}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          cursor: "pointer",
                          transition: "transform 0.3s ease",
                          transform: chapter.open ? "rotate(90deg)" : "rotate(0deg)",
                        }}
                      >
                        <ChevronRight />
                      </Box>
                    ) : (
                      <Description fontSize="small" />
                    )}
                  </ListItemIcon>

                  <ListItemIcon
                    sx={{
                      minWidth: 30,
                      color: isSelected ? theme.palette.primary.main : theme.palette.text.secondary,
                    }}
                  >
                    {hasSubChapters ? (
                      chapter.open ? (
                        <FolderOpen fontSize="small" />
                      ) : (
                        <Folder fontSize="small" />
                      )
                    ) : null}
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      chapter.type === ChapterType.APPENDIX ||
                      chapter.type === ChapterType.APPENDIX_A
                        ? `${chapter.chapterNumber} - ${chapter.name}` // For appendix chapters: "Appendix A - Applied Conformity Assessment Rule"
                        : `${chapter.chapterNumber}${chapter.chapterNumber ? " " : ""}${chapter.name}` // For regular chapters: "1 Index"
                    }
                    primaryTypographyProps={{
                      fontSize: depth === 0 ? "0.95rem" : "0.9rem",
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected ? theme.palette.primary.main : "inherit",
                    }}
                  />
                </ListItemButton>

                <Collapse
                  in={chapter.open}
                  timeout={{
                    enter: 300,
                    exit: 200,
                  }}
                  unmountOnExit
                >
                  {hasSubChapters &&
                    renderChapters(
                      chapter.subChapter!.filter(
                        (subChapter): subChapter is Chapter => subChapter !== null
                      ),
                      depth + 1,
                      chapter.examinationId || examId
                    )}
                </Collapse>
              </Collapse>
            )
          })}
        </TransitionGroup>
      </List>
    )
  }

  return (
    <Box
      sx={{
        backgroundColor: alpha(theme.palette.background.default, 0.3),
        borderRadius: 2,
        p: 1,
        maxHeight: "400px",
        overflowY: "auto",
        overflowX: "hidden",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: alpha(theme.palette.background.default, 0.1),
          borderRadius: "4px",
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: alpha(theme.palette.primary.main, 0.2),
          borderRadius: "4px",
          "&:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.3),
          },
        },
      }}
    >
      {renderChapters(chapters)}
    </Box>
  )
}
